import { useEffect, useRef, useState } from 'react';
import { usePresentationContext } from '../core/PresentationContext';

interface ImageCarouselProps {
  images: string[];
  speed?: number; // Pixels per frame
  className?: string;
}

export const ImageCarousel = ({ images, speed = 0.002, className = '' }: ImageCarouselProps) => {
  const { deck, isInitialized } = usePresentationContext();
  const [position, setPosition] = useState(0);
  const [displayImages, setDisplayImages] = useState([...images, ...images, ...images, ...images]);
  const [shouldRender, setShouldRender] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  // Handle animation
  useEffect(() => {
    if (!deck || !isInitialized) return;

    const startAnimation = () => {
      console.log('[ImageCarousel] Starting animation');

      const moveImages = () => {
        setPosition((prev) => {
          const newPosition = prev - speed;
          if (newPosition <= -25) {
            setDisplayImages((current) => {
              const newImages = [...current];
              const first = newImages.shift()!;
              newImages.push(first);
              return newImages;
            });
            return 0;
          }
          return newPosition;
        });
        animationRef.current = requestAnimationFrame(moveImages);
      };

      animationRef.current = requestAnimationFrame(moveImages);
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    };

    const handleSlideChange = () => {
      const currentSlide = deck.getCurrentSlide();
      if (!currentSlide || !containerRef.current) return;

      // Only start animation if we're in the current slide
      if (currentSlide.contains(containerRef.current)) {
        console.log('[ImageCarousel] Entering slide');
        // Quick unmount-remount cycle
        setShouldRender(false);
        requestAnimationFrame(() => {
          setShouldRender(true);
          // Reset position and start animation
          setPosition(0);
          cleanupRef.current = startAnimation();
        });
      } else {
        // console.log('[ImageCarousel] Leaving slide');
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = undefined;
        }
      }
    };

    deck.on('slidechanged', handleSlideChange);

    // Check initial slide
    handleSlideChange();

    return () => {
      deck.off('slidechanged', handleSlideChange);
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = undefined;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [deck, isInitialized, speed]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div ref={containerRef} className="flex items-center justify-center h-full overflow-hidden">
      <div
        className="flex gap-4"
        style={{
          transform: `translateX(${position}%)`,
          transition: 'transform 16ms linear',
        }}
      >
        {displayImages.map((image, index) => (
          <div
            key={`${image}-${index}`}
            className={`
              h-[80vh]
              w-[20vw]
              shrink-0
              bg-black/50
              rounded-xl
              overflow-hidden
              transform
              hover:scale-105
              transition-transform
              ${className}
            `}
          >
            <img
              src={image}
              alt={`Carousel image ${index + 1}`}
              className="h-full w-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
};
